// Device credentials (plan §10.2, D-042). Pairing: a branch code and a one-time access key
// (typed, or scanned as `PAIR:<branch>:<key>`) → `POST /devices/register` → a refresh secret
// kept in the platform's secure storage. The secret buys short-lived device JWTs
// (`POST /devices/token`, 10 minutes, D-050), refreshed before they expire. A 401 on refresh
// means the device was revoked or its secret is dead: the station must be paired again
// ("re-pair" state). So must a station whose credentials survived but whose local database
// did not (an iOS reinstall, review CR3-4): its numbering would start again at 0, so it gets
// a new device and device code instead.
//
// A 426 from register or token means this app version is too old (D-050): the caller's
// `onUpgradeRequired` raises the minimum-version gate before the error is thrown, so the
// station says "update needed" even when it never gets a token (review CR3-1).
//
// Re-pairing keeps the local outbox: queued commands still carry the old `device_id`, and
// the server accepts them from a later device of the same station (D-024). Pairing into a
// different account is refused while commands wait (the caller's `guard`, review CR3-7).

import {
  type DevicePlatform,
  type DeviceRegisterResponse,
  type DeviceTokenResponse,
  type StationProfile,
  type UpgradeNotice,
  normaliseAccessKey,
  parsePairingQr,
  upgradeNoticeFrom,
} from '@enp/domain';
import { ApiError, type ApiClient } from './api.js';
import type { SecureStore } from './secure-store.js';
import { Store } from './util.js';

/** What the station knows about its own pairing (the secret stays in secure storage). */
export interface PairedDevice {
  deviceId: string;
  deviceCode: string;
  accountId: string;
  branchId: string;
  stationId: string;
  stationName: string;
  profile: StationProfile;
  pairedAt: string;
}

/** Why a paired station must be paired again. */
export type DeadReason = 'device_revoked' | 'invalid_credentials' | 'local_data_lost';

export type PairingState =
  | { status: 'loading' }
  | { status: 'unpaired' }
  | { status: 'paired'; device: PairedDevice }
  /**
   * Pair again: the server no longer accepts this device (revoked, or its secret is dead), or
   * the local database was lost while the credentials survived (`local_data_lost`).
   */
  | { status: 'repair_required'; device: PairedDevice; reason: DeadReason };

/** Why pairing failed, in terms the pairing screen can act on. */
export type PairingFailure =
  | 'invalid_input'
  | 'pairing_invalid'
  | 'rate_limited'
  | 'upgrade_required'
  | 'offline'
  | 'server_error';

export class PairingError extends Error {
  constructor(
    readonly reason: PairingFailure,
    message: string,
    readonly minAppVersion?: string,
  ) {
    super(message);
    this.name = 'PairingError';
  }
}

/** Uploads and token requests stop until the station is paired again. */
export class RepairRequiredError extends Error {
  constructor(readonly reason: DeadReason | 'not_paired') {
    super(
      reason === 'not_paired'
        ? 'This station is not paired yet'
        : reason === 'local_data_lost'
          ? 'This station lost its local data and must be paired again'
          : 'This station must be paired again (the server no longer accepts it)',
    );
    this.name = 'RepairRequiredError';
  }
}

/** The station app is below the minimum version (426 on register or token). */
export class UpgradeRequiredError extends Error {
  readonly minAppVersion: string | undefined;
  constructor(readonly notice: UpgradeNotice) {
    const min = notice.min_app_version ?? undefined;
    super(`This station app must be updated${min ? ` (to ${min} or later)` : ''}`);
    this.name = 'UpgradeRequiredError';
    this.minAppVersion = min;
  }
}

/** The notice in a 426 answer (a bare 426 still means "update the app"). */
export function upgradeNoticeOf(err: ApiError): UpgradeNotice {
  return upgradeNoticeFrom(err.body) ?? { reason: 'app_version', min_app_version: null };
}

interface StoredCredentials extends PairedDevice {
  refreshSecret: string;
  /** Set when the station must pair again: the old identity is still shown. */
  deadReason?: DeadReason;
}

const STORE_KEY = 'enp.device.credentials';
/**
 * A registration held back for another account (B-039, review CR3-24): kept in secure
 * storage, so a restart doesn't strand the device the one-time key registered.
 */
const PENDING_KEY = 'enp.device.pending';

function parseCreds(raw: string | null): StoredCredentials | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredCredentials;
  } catch {
    return null;
  }
}

export interface DeviceAuthOptions {
  api: ApiClient;
  store: SecureStore;
  platform: DevicePlatform;
  appVersion: string;
  now?: () => Date;
  /** Refresh a token this long before it expires. Default 2 minutes. */
  refreshMarginMs?: number;
  /**
   * Told about every 426 from register or token, before the error is thrown: the station
   * raises its minimum-version gate here (review CR3-1).
   */
  onUpgradeRequired?: (notice: UpgradeNotice) => void | Promise<void>;
}

export interface PairOptions {
  /**
   * Runs after the server registered the device and before the station adopts it. Throwing
   * keeps the old pairing; the registration is kept (in secure storage, across restarts)
   * for {@link DeviceAuth.adoptPending}, so the one-time key isn't wasted.
   */
  guard?: (next: PairedDevice) => Promise<void>;
}

export type PairInput = { branchCode: string; accessKey: string } | { qr: string };

/**
 * When a device token expires by **this device's** clock: its lifetime (the JWT's `exp` − `iat`,
 * set by the server's clock) counted from when it arrived here. `expires_at` is the server's
 * instant; compared with a device clock that is off (plan §9.5 "device clock wrong"), a tablet
 * hours behind kept handing the sync service a token it already refused (downloads stopped
 * until the tablet's clock caught up), and one ahead threw every fresh token away (WP8.1).
 * A token that is not a readable JWT falls back to `expires_at`.
 */
export function localTokenExpiry(
  token: Pick<DeviceTokenResponse, 'token' | 'expires_at'>,
  receivedAt: Date,
): number {
  const payload = token.token.split('.')[1];
  if (payload) {
    try {
      const claims = JSON.parse(new TextDecoder().decode(base64UrlBytes(payload))) as {
        iat?: unknown;
        exp?: unknown;
      };
      if (
        typeof claims.iat === 'number' &&
        typeof claims.exp === 'number' &&
        claims.exp > claims.iat
      ) {
        return receivedAt.getTime() + (claims.exp - claims.iat) * 1000;
      }
    } catch {
      // not a JWT: use expires_at
    }
  }
  return Date.parse(token.expires_at);
}

function base64UrlBytes(text: string): Uint8Array {
  const b64 = text
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(text.length / 4) * 4, '=');
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export class DeviceAuth {
  readonly #api: ApiClient;
  readonly #store: SecureStore;
  readonly #platform: DevicePlatform;
  readonly #appVersion: string;
  readonly #now: () => Date;
  readonly #marginMs: number;
  readonly #onUpgradeRequired: DeviceAuthOptions['onUpgradeRequired'];
  readonly state = new Store<{ pairing: PairingState }>({ pairing: { status: 'loading' } });
  #creds: StoredCredentials | null = null;
  #token: DeviceTokenResponse | null = null;
  /** When `#token` expires by this device's clock ({@link localTokenExpiry}). */
  #tokenExpiresAt = 0;
  /** The refresh in flight, for the pairing generation it was started for. */
  #refreshing: { generation: number; promise: Promise<DeviceTokenResponse> } | null = null;
  /**
   * Counts pairings adopted or forgotten (review CR8B-2). A refresh started for one pairing
   * never writes its token, or its "must pair again", once the station has another.
   */
  #generation = 0;
  /** A registration the guard held back (see {@link PairOptions.guard}). */
  #pending: StoredCredentials | null = null;

  constructor(options: DeviceAuthOptions) {
    this.#api = options.api;
    this.#store = options.store;
    this.#platform = options.platform;
    this.#appVersion = options.appVersion;
    this.#now = options.now ?? (() => new Date());
    this.#marginMs = options.refreshMarginMs ?? 120_000;
    this.#onUpgradeRequired = options.onUpgradeRequired;
  }

  get pairing(): PairingState {
    return this.state.get().pairing;
  }

  /**
   * A stored value, or null when it can't be read (review CR8B-13): secure storage that can no
   * longer decrypt it (a new Windows profile, a restored disk) means pairing again, not a
   * station that never starts.
   */
  async #read(key: string): Promise<string | null> {
    try {
      return await this.#store.get(key);
    } catch (err) {
      console.error(`The station credentials (${key}) can't be read; pair the station again`, err);
      return null;
    }
  }

  /** The paired device, also while it must be paired again. */
  get device(): PairedDevice | null {
    return this.#creds ? toPublic(this.#creds) : null;
  }

  /** Reads the stored credentials (and a registration held back for another account). */
  async load(): Promise<PairingState> {
    this.#creds = parseCreds(await this.#read(STORE_KEY));
    this.#pending = parseCreds(await this.#read(PENDING_KEY));
    this.#publish();
    return this.pairing;
  }

  /**
   * Pairs this install with a station. On success the refresh secret is in secure storage
   * and the state is `paired`. Throws {@link PairingError}, or what `guard` throws.
   */
  async pair(input: PairInput, options: PairOptions = {}): Promise<PairedDevice> {
    const parsed =
      'qr' in input
        ? parsePairingQr(input.qr)
        : (() => {
            const key = normaliseAccessKey(input.accessKey);
            const branchCode = input.branchCode.trim();
            return key && branchCode ? { branchCode, accessKey: key } : null;
          })();
    if (!parsed) {
      throw new PairingError(
        'invalid_input',
        'That is not a branch code and access key. Check both and try again.',
      );
    }
    let registered: DeviceRegisterResponse;
    try {
      registered = await this.#api.register({
        branch_code: parsed.branchCode,
        access_key: parsed.accessKey,
        platform: this.#platform,
        app_version: this.#appVersion,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 426) {
        await this.#onUpgradeRequired?.(upgradeNoticeOf(err));
      }
      throw toPairingError(err);
    }
    const creds: StoredCredentials = {
      deviceId: registered.device_id,
      deviceCode: registered.device_code,
      accountId: registered.account_id,
      branchId: registered.branch_id,
      stationId: registered.station_id,
      stationName: registered.station_name,
      profile: registered.profile,
      pairedAt: this.#now().toISOString(),
      refreshSecret: registered.refresh_secret,
    };
    await this.#setPending(null);
    if (options.guard) {
      try {
        await options.guard(toPublic(creds));
      } catch (err) {
        await this.#setPending(creds);
        throw err;
      }
    }
    return this.#adopt(creds);
  }

  /** The registration a guard held back, if any. */
  get pendingDevice(): PairedDevice | null {
    return this.#pending ? toPublic(this.#pending) : null;
  }

  /** Adopts the registration a guard held back (e.g. once the station was wiped). */
  async adoptPending(): Promise<PairedDevice | null> {
    const creds = this.#pending;
    await this.#setPending(null);
    return creds ? this.#adopt(creds) : null;
  }

  /**
   * Forgets a held-back registration: the key it used is spent, so pairing with that
   * station later needs a new key from the office.
   */
  async dropPending(): Promise<void> {
    await this.#setPending(null);
  }

  async #setPending(creds: StoredCredentials | null): Promise<void> {
    this.#pending = creds;
    if (creds) await this.#store.set(PENDING_KEY, JSON.stringify(creds));
    else await this.#store.delete(PENDING_KEY);
  }

  /**
   * The local database was lost but the credentials survived (review CR3-4): the station
   * must pair again, so its numbers never repeat. Kept across restarts.
   */
  async markLocalDataLost(): Promise<void> {
    const creds = this.#creds;
    if (!creds || creds.deadReason) return;
    const dead: StoredCredentials = { ...creds, deadReason: 'local_data_lost' };
    await this.#store.set(STORE_KEY, JSON.stringify(dead));
    this.#creds = dead;
    this.#newGeneration();
    this.#publish();
  }

  /** Forgets the pairing (the local outbox is kept). */
  async forget(): Promise<void> {
    this.#newGeneration();
    await this.#store.delete(STORE_KEY);
    this.#creds = null;
    this.#newGeneration();
    this.#publish();
  }

  /** A new pairing (or none): drop the token and any refresh in flight for the old one. */
  #newGeneration() {
    this.#generation++;
    this.#token = null;
    this.#tokenExpiresAt = 0;
    this.#refreshing = null;
  }

  /**
   * When the current token expires by this device's clock (for the sync SDK's own refresh),
   * or null without one.
   */
  get tokenExpiresAt(): Date | null {
    return this.#token ? new Date(this.#tokenExpiresAt) : null;
  }

  /** Drops the cached token (e.g. after a 401 from the API); the next call refreshes. */
  invalidateToken(): void {
    this.#token = null;
  }

  /**
   * A device JWT valid for at least the refresh margin. Refreshes when needed (one request at
   * a time). Throws {@link RepairRequiredError} when unpaired or refused,
   * {@link UpgradeRequiredError} on 426, {@link ApiError} otherwise (offline: `isNetwork`).
   */
  async getToken(): Promise<DeviceTokenResponse> {
    const creds = this.#creds;
    if (!creds) throw new RepairRequiredError('not_paired');
    if (creds.deadReason) throw new RepairRequiredError(creds.deadReason);
    const token = this.#token;
    if (token && this.#tokenExpiresAt - this.#now().getTime() > this.#marginMs) {
      return token;
    }
    const generation = this.#generation;
    if (this.#refreshing?.generation !== generation) {
      const promise = this.#refresh(creds, generation).finally(() => {
        if (this.#refreshing?.promise === promise) this.#refreshing = null;
      });
      this.#refreshing = { generation, promise };
    }
    return this.#refreshing.promise;
  }

  /** Whether `creds` is still this station's pairing (nothing was adopted or forgotten). */
  #current(creds: StoredCredentials, generation: number): boolean {
    return this.#generation === generation && this.#creds === creds;
  }

  async #adopt(creds: StoredCredentials): Promise<PairedDevice> {
    // First: a refresh in flight for the old pairing is stale from here on (CR8B-2).
    this.#newGeneration();
    await this.#store.set(STORE_KEY, JSON.stringify(creds));
    this.#creds = creds;
    this.#newGeneration();
    this.#publish();
    return toPublic(creds);
  }

  async #refresh(creds: StoredCredentials, generation: number): Promise<DeviceTokenResponse> {
    let token: DeviceTokenResponse;
    try {
      token = await this.#api.token({
        device_id: creds.deviceId,
        refresh_secret: creds.refreshSecret,
        app_version: this.#appVersion,
      });
    } catch (err) {
      // Re-paired (or forgotten) meanwhile: this answer was about the old device. It never
      // marks the new pairing dead; ask again for the current one.
      if (!this.#current(creds, generation)) return this.getToken();
      if (err instanceof ApiError && err.status === 401) {
        const reason = err.code === 'device_revoked' ? 'device_revoked' : 'invalid_credentials';
        const dead: StoredCredentials = { ...creds, deadReason: reason };
        await this.#store.set(STORE_KEY, JSON.stringify(dead));
        this.#creds = dead;
        this.#token = null;
        this.#publish();
        throw new RepairRequiredError(reason);
      }
      if (err instanceof ApiError && err.status === 426) {
        const notice = upgradeNoticeOf(err);
        await this.#onUpgradeRequired?.(notice);
        throw new UpgradeRequiredError(notice);
      }
      throw err;
    }
    // Re-paired (or forgotten) while this was in flight: the token is for the old device,
    // maybe another account. Never keep or hand it out; ask again for the current one.
    if (!this.#current(creds, generation)) return this.getToken();
    if (token.device_id !== creds.deviceId || token.account_id !== creds.accountId) {
      throw new ApiError('contract', '/devices/token: a token for another device', 200);
    }
    this.#token = token;
    this.#tokenExpiresAt = localTokenExpiry(token, this.#now());
    return token;
  }

  #publish() {
    const c = this.#creds;
    const pairing: PairingState = !c
      ? { status: 'unpaired' }
      : c.deadReason
        ? { status: 'repair_required', device: toPublic(c), reason: c.deadReason }
        : { status: 'paired', device: toPublic(c) };
    this.state.set({ pairing });
  }
}

function toPublic(c: StoredCredentials): PairedDevice {
  return {
    deviceId: c.deviceId,
    deviceCode: c.deviceCode,
    accountId: c.accountId,
    branchId: c.branchId,
    stationId: c.stationId,
    stationName: c.stationName,
    profile: c.profile,
    pairedAt: c.pairedAt,
  };
}

function toPairingError(err: unknown): PairingError {
  if (!(err instanceof ApiError)) {
    return new PairingError('server_error', 'Pairing failed. Try again.');
  }
  if (err.isNetwork) {
    return new PairingError(
      'offline',
      'The station can’t reach the server. Check the network, then try again.',
    );
  }
  switch (err.status) {
    case 400:
    case 401:
    case 404:
      return new PairingError(
        'pairing_invalid',
        'That branch code and access key don’t match an open pairing. Ask for a new key in the Admin.',
      );
    case 426:
      return new PairingError(
        'upgrade_required',
        'This station app is too old to pair. Update it first.',
        upgradeNoticeOf(err).min_app_version ?? undefined,
      );
    case 429:
      return new PairingError(
        'rate_limited',
        'Too many tries from this station. Wait a few minutes, then try again.',
      );
    default:
      return new PairingError('server_error', 'The server couldn’t pair this station. Try again.');
  }
}
